import { endOfDay, isWithinInterval, parseISO, startOfDay } from 'date-fns';
import { Expense } from '../types/expenseTypes';
import { ExpenseFilterState, FilterOption, HierarchicalFilterOption } from '../types/filterTypes';

/**
 * Unified Filter Utilities for Expense Tracker Enterprise
 * Δ3: DATA FILTERING STANDARDIZATION
 *
 * This module provides standardized filtering logic that can be used across
 * all components (ExpenseFilterPanel, FilterBar, EnterpriseDataTable, etc.)
 */

// Core filter application function
export function applyExpenseFilters(
  expenses: Expense[],
  filters: ExpenseFilterState,
  masterData?: {
    types?: Array<{ id: string; name: string }>;
    units?: Array<{ id: string; name: string }>;
    costCenters?: Array<{ id: string; name: string }>;
    categories?: Array<{ id: string; name: string }>;
  }
): Expense[] {
  return expenses.filter(expense => {
    // Date range filter
    if (filters.dateRange) {
      const expenseDate = parseISO(typeof expense.date === 'string' ? expense.date : expense.date?.toISOString() || '');
      const startDate = startOfDay(parseISO(filters.dateRange.start));
      const endDate = endOfDay(parseISO(filters.dateRange.end));

      if (!isWithinInterval(expenseDate, { start: startDate, end: endDate })) {
        return false;
      }
    }

    // Type filter - Handle both old and new data formats
    if (filters.type) {
      let expenseTypeName = '';

      // First try new format (typeId lookup)
      if (expense.typeId) {
        expenseTypeName = masterData?.types?.find(t => t.id === expense.typeId)?.name || '';
      }

      // If no typeId or lookup failed, try old format (type field lookup)
      if (!expenseTypeName && expense.type) {
        // Check if type field contains an ID (old DEV data)
        expenseTypeName = masterData?.types?.find(t => t.id === expense.type)?.name ||
          // Check if type field contains a name
          masterData?.types?.find(t => t.name === expense.type)?.name ||
          // Fallback to the type field value itself
          expense.type;
      }

      if (expenseTypeName !== filters.type) {
        return false;
      }
    }

    // Units filter (multi-select) - Handle both old and new data formats
    if (filters.units && filters.units.length > 0) {
      let expenseUnitName = '';

      // First try new format (unitId lookup)
      if (expense.unitId) {
        expenseUnitName = masterData?.units?.find(u => u.id === expense.unitId)?.name || '';
      }

      // If no unitId or lookup failed, try old format (unit field lookup)
      if (!expenseUnitName && expense.unit) {
        // Check if unit field contains an ID (old DEV data)
        expenseUnitName = masterData?.units?.find(u => u.id === expense.unit)?.name ||
          // Check if unit field contains a name
          masterData?.units?.find(u => u.name === expense.unit)?.name ||
          // Fallback to the unit field value itself
          expense.unit;
      }

      if (!filters.units.includes(expenseUnitName || '')) {
        return false;
      }
    }

    // Cost centers filter (multi-select) - Handle both old and new data formats
    if (filters.costCenters && filters.costCenters.length > 0) {
      let expenseCostCenterName = '';

      // First try new format (costCenterId lookup)
      if (expense.costCenterId) {
        expenseCostCenterName = masterData?.costCenters?.find(c => c.id === expense.costCenterId)?.name || '';
      }

      // If no costCenterId or lookup failed, try old format (costCenter field lookup)
      if (!expenseCostCenterName && expense.costCenter) {
        // Check if costCenter field contains an ID (old DEV data)
        expenseCostCenterName = masterData?.costCenters?.find(c => c.id === expense.costCenter)?.name ||
          // Check if costCenter field contains a name
          masterData?.costCenters?.find(c => c.name === expense.costCenter)?.name ||
          // Fallback to the costCenter field value itself
          expense.costCenter;
      }

      if (!filters.costCenters.includes(expenseCostCenterName || '')) {
        return false;
      }
    }

    // Categories filter (multi-select) - Handle both old and new data formats
    if (filters.categories && filters.categories.length > 0) {
      let expenseCategoryName = '';

      // First try new format (categoryId lookup)
      if (expense.categoryId) {
        expenseCategoryName = masterData?.categories?.find(cat => cat.id === expense.categoryId)?.name || '';
      }

      // If no categoryId or lookup failed, try old format (category field lookup)
      if (!expenseCategoryName && expense.category) {
        // Check if category field contains an ID (old DEV data)
        expenseCategoryName = masterData?.categories?.find(cat => cat.id === expense.category)?.name ||
          // Check if category field contains a name
          masterData?.categories?.find(cat => cat.name === expense.category)?.name ||
          // Fallback to the category field value itself
          expense.category;
      }

      if (!filters.categories.includes(expenseCategoryName || '')) {
        return false;
      }
    }

    // Shop filter
    if (filters.shop && expense.shop !== filters.shop) {
      return false;
    }

    // Nature filter
    if (filters.nature && expense.nature !== filters.nature) {
      return false;
    }

    // Amount range filter
    if (filters.amountRange) {
      const amount = typeof expense.amount === 'number' ? expense.amount :
        typeof expense.amount === 'string' ? parseFloat(expense.amount) : 0;
      if (amount < filters.amountRange.min || amount > filters.amountRange.max) {
        return false;
      }
    }

    // Approval status filter
    if (filters.approvalStatus && filters.approvalStatus !== 'all') {
      if (expense.approvalStatus !== filters.approvalStatus) {
        return false;
      }
    }

    // Created by filter
    if (filters.createdBy && expense.createdBy !== filters.createdBy) {
      return false;
    }

    // Search term filter (searches across multiple fields)
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const searchableFields = [
        expense.type,
        expense.unit,
        expense.costCenter,
        expense.category,
        expense.shop,
        expense.description,
        expense.createdBy,
        expense.amount?.toString(),
        expense.approvalStatus
      ].filter(Boolean);

      const matchesSearch = searchableFields.some(field =>
        field?.toString().toLowerCase().includes(searchLower)
      );

      if (!matchesSearch) {
        return false;
      }
    }

    return true;
  });
}

// Generate filter options from expense data
export function generateFilterOptions(
  expenses: Expense[],
  masterData?: {
    types?: Array<{ id: string; name: string }>;
    units?: Array<{ id: string; name: string }>;
    costCenters?: Array<{ id: string; name: string }>;
    categories?: Array<{ id: string; name: string }>;
  }
): {
  types: FilterOption[];
  units: HierarchicalFilterOption[];
  costCenters: HierarchicalFilterOption[];
  categories: HierarchicalFilterOption[];
  shops: FilterOption[];
  createdBy: FilterOption[];
} {
  const typeMap = new Map<string, number>();
  const unitMap = new Map<string, number>();
  const costCenterMap = new Map<string, { count: number; typeId?: string; unitId?: string }>();
  const categoryMap = new Map<string, { count: number; costCenterId?: string; typeId?: string }>();
  const shopMap = new Map<string, number>();
  const createdByMap = new Map<string, number>();

  expenses.forEach(expense => {
    // Type counting - Handle both old and new data formats
    let typeName = '';

    // First try new format (typeId lookup)
    if (expense.typeId) {
      typeName = masterData?.types?.find(t => t.id === expense.typeId)?.name || '';
    }

    // If no typeId or lookup failed, try old format (type field lookup)
    if (!typeName && expense.type) {
      // Check if type field contains an ID (old DEV data)
      typeName = masterData?.types?.find(t => t.id === expense.type)?.name ||
        // Check if type field contains a name
        masterData?.types?.find(t => t.name === expense.type)?.name ||
        // Fallback to the type field value itself
        expense.type;
    }

    if (typeName) {
      typeMap.set(typeName, (typeMap.get(typeName) || 0) + 1);
    }

    // Unit counting - Handle both old and new data formats
    let unitName = '';

    // First try new format (unitId lookup)
    if (expense.unitId) {
      unitName = masterData?.units?.find(u => u.id === expense.unitId)?.name || '';
    }

    // If no unitId or lookup failed, try old format (unit field lookup)
    if (!unitName && expense.unit) {
      // Check if unit field contains an ID (old DEV data)
      unitName = masterData?.units?.find(u => u.id === expense.unit)?.name ||
        // Check if unit field contains a name
        masterData?.units?.find(u => u.name === expense.unit)?.name ||
        // Fallback to the unit field value itself
        expense.unit;
    }

    if (unitName) {
      unitMap.set(unitName, (unitMap.get(unitName) || 0) + 1);
    }

    // Cost center counting with hierarchy - Handle both old and new data formats
    let costCenterName = '';

    // First try new format (costCenterId lookup)
    if (expense.costCenterId) {
      costCenterName = masterData?.costCenters?.find(c => c.id === expense.costCenterId)?.name || '';
    }

    // If no costCenterId or lookup failed, try old format (costCenter field lookup)
    if (!costCenterName && expense.costCenter) {
      // Check if costCenter field contains an ID (old DEV data)
      costCenterName = masterData?.costCenters?.find(c => c.id === expense.costCenter)?.name ||
        // Check if costCenter field contains a name
        masterData?.costCenters?.find(c => c.name === expense.costCenter)?.name ||
        // Fallback to the costCenter field value itself
        expense.costCenter;
    }

    if (costCenterName) {
      const existing = costCenterMap.get(costCenterName) || { count: 0 };
      costCenterMap.set(costCenterName, {
        count: existing.count + 1,
        typeId: expense.typeId || expense.type,
        unitId: expense.unitId || expense.unit
      });
    }

    // Category counting with hierarchy - Handle both old and new data formats
    let categoryName = '';

    // First try new format (categoryId lookup)
    if (expense.categoryId) {
      categoryName = masterData?.categories?.find(cat => cat.id === expense.categoryId)?.name || '';
    }

    // If no categoryId or lookup failed, try old format (category field lookup)
    if (!categoryName && expense.category) {
      // Check if category field contains an ID (old DEV data)
      categoryName = masterData?.categories?.find(cat => cat.id === expense.category)?.name ||
        // Check if category field contains a name
        masterData?.categories?.find(cat => cat.name === expense.category)?.name ||
        // Fallback to the category field value itself
        expense.category;
    }

    if (categoryName) {
      const existing = categoryMap.get(categoryName) || { count: 0 };
      categoryMap.set(categoryName, {
        count: existing.count + 1,
        costCenterId: expense.costCenterId || expense.costCenter,
        typeId: expense.typeId || expense.type
      });
    }

    // Shop counting
    if (expense.shop) {
      shopMap.set(expense.shop, (shopMap.get(expense.shop) || 0) + 1);
    }

    // Created by counting
    if (expense.createdBy) {
      createdByMap.set(expense.createdBy, (createdByMap.get(expense.createdBy) || 0) + 1);
    }
  });

  return {
    types: Array.from(typeMap.entries()).map(([value, count]) => ({
      label: value,
      value,
      count
    })).sort((a, b) => b.count - a.count),

    units: Array.from(unitMap.entries()).map(([value, count]) => ({
      label: value,
      value,
      count,
      level: 2
    })).sort((a, b) => b.count - a.count),

    costCenters: Array.from(costCenterMap.entries()).map(([value, data]) => ({
      label: value,
      value,
      count: data.count,
      level: 3,
      parentId: data.typeId
    })).sort((a, b) => b.count - a.count),

    categories: Array.from(categoryMap.entries()).map(([value, data]) => ({
      label: value,
      value,
      count: data.count,
      level: 4,
      parentId: data.costCenterId
    })).sort((a, b) => b.count - a.count),

    shops: Array.from(shopMap.entries()).map(([value, count]) => ({
      label: value,
      value,
      count
    })).sort((a, b) => b.count - a.count),

    createdBy: Array.from(createdByMap.entries()).map(([value, count]) => ({
      label: value,
      value,
      count
    })).sort((a, b) => b.count - a.count)
  };
}

// Filter state management utilities
export function createDefaultExpenseFilterState(): ExpenseFilterState {
  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);

  return {
    dateRange: {
      start: thirtyDaysAgo.toISOString().split('T')[0],
      end: today.toISOString().split('T')[0]
    },
    searchTerm: '',
    sortBy: 'date',
    sortOrder: 'desc',
    approvalStatus: 'all'
  };
}

export function isFilterActive(filters: ExpenseFilterState): boolean {
  const defaultState = createDefaultExpenseFilterState();

  return (
    filters.searchTerm !== defaultState.searchTerm ||
    filters.type !== defaultState.type ||
    (filters.units?.length || 0) > 0 ||
    (filters.costCenters?.length || 0) > 0 ||
    (filters.categories?.length || 0) > 0 ||
    filters.shop !== defaultState.shop ||
    filters.amountRange !== defaultState.amountRange ||
    filters.approvalStatus !== defaultState.approvalStatus ||
    filters.createdBy !== defaultState.createdBy ||
    filters.dateRange.start !== defaultState.dateRange.start ||
    filters.dateRange.end !== defaultState.dateRange.end
  );
}

export function clearAllFilters(): ExpenseFilterState {
  return createDefaultExpenseFilterState();
}

// Quick date range presets
export const DATE_RANGE_PRESETS = {
  today: {
    label: 'Today',
    getValue: () => {
      const today = new Date();
      return {
        start: today.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      };
    }
  },
  yesterday: {
    label: 'Yesterday',
    getValue: () => {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        start: yesterday.toISOString().split('T')[0],
        end: yesterday.toISOString().split('T')[0]
      };
    }
  },
  last7days: {
    label: 'Last 7 Days',
    getValue: () => {
      const today = new Date();
      const weekAgo = new Date();
      weekAgo.setDate(today.getDate() - 7);
      return {
        start: weekAgo.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      };
    }
  },
  last30days: {
    label: 'Last 30 Days',
    getValue: () => {
      const today = new Date();
      const monthAgo = new Date();
      monthAgo.setDate(today.getDate() - 30);
      return {
        start: monthAgo.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      };
    }
  },
  thisMonth: {
    label: 'This Month',
    getValue: () => {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      return {
        start: firstDay.toISOString().split('T')[0],
        end: today.toISOString().split('T')[0]
      };
    }
  },
  lastMonth: {
    label: 'Last Month',
    getValue: () => {
      const today = new Date();
      const firstDayLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const lastDayLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        start: firstDayLastMonth.toISOString().split('T')[0],
        end: lastDayLastMonth.toISOString().split('T')[0]
      };
    }
  }
};

// Sorting utilities
export function sortExpenses(
  expenses: Expense[],
  sortBy: string = 'date',
  sortOrder: 'asc' | 'desc' = 'desc'
): Expense[] {
  return [...expenses].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortBy) {
      case 'date':
        aValue = new Date(typeof a.date === 'string' ? a.date : a.date?.toISOString() || '');
        bValue = new Date(typeof b.date === 'string' ? b.date : b.date?.toISOString() || '');
        break;
      case 'amount':
        aValue = typeof a.amount === 'number' ? a.amount :
          typeof a.amount === 'string' ? parseFloat(a.amount) : 0;
        bValue = typeof b.amount === 'number' ? b.amount :
          typeof b.amount === 'string' ? parseFloat(b.amount) : 0;
        break;
      case 'type':
        aValue = a.type || '';
        bValue = b.type || '';
        break;
      case 'unit':
        aValue = a.unit || '';
        bValue = b.unit || '';
        break;
      case 'costCenter':
        aValue = a.costCenter || '';
        bValue = b.costCenter || '';
        break;
      case 'category':
        aValue = a.category || '';
        bValue = b.category || '';
        break;
      case 'createdBy':
        aValue = a.createdBy || '';
        bValue = b.createdBy || '';
        break;
      default:
        aValue = a.date;
        bValue = b.date;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
}

// Filter state persistence (localStorage)
const FILTER_STORAGE_KEY = 'expense-tracker-filters';
const DEFAULT_FILTERS_KEY = 'expense-tracker-default-filters';

export function saveFilterState(filters: ExpenseFilterState): void {
  try {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.warn('Failed to save filter state:', error);
  }
}

export function loadFilterState(): ExpenseFilterState | null {
  try {
    const stored = localStorage.getItem(FILTER_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load filter state:', error);
    return null;
  }
}

export function clearSavedFilterState(): void {
  try {
    localStorage.removeItem(FILTER_STORAGE_KEY);
  } catch (error) {
    console.warn('Failed to clear filter state:', error);
  }
}

// Default filter preferences persistence
export function saveDefaultFilterPreferences(preferences: {
  dateRange: { start: string; end: string };
  approvalStatus: string;
  type: string;
  unit: string;
}): void {
  try {
    localStorage.setItem(DEFAULT_FILTERS_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.warn('Failed to save default filter preferences:', error);
  }
}

export function loadDefaultFilterPreferences(): {
  dateRange: { start: string; end: string };
  approvalStatus: string;
  type: string;
  unit: string;
} | null {
  try {
    const stored = localStorage.getItem(DEFAULT_FILTERS_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn('Failed to load default filter preferences:', error);
    return null;
  }
}

export function clearDefaultFilterPreferences(): void {
  try {
    localStorage.removeItem(DEFAULT_FILTERS_KEY);
  } catch (error) {
    console.warn('Failed to clear default filter preferences:', error);
  }
}
